import { alpha, createTheme, darken } from '@mui/material/styles';
import { surface, brand, ink, line, status } from './tokens';

/**
 * ADMIN THEME — brown-on-white theme for /admin/* routes.
 *
 * Every colour comes from src/theme/tokens.js, which is contrast-checked in CI
 * (scripts/check-contrast.js). The admin surface is `surface.card` (#FFFFFF),
 * not the cream page, so the ratios quoted below are against white.
 *
 * What was wrong before this pass: the palette carried MUI's default
 * success/warning/error/info swatches, which are decorative-bright and fail AA
 * as text on white — #4caf50 is 2.78:1, #ff9800 is 2.16:1, #f44336 is 3.68:1,
 * #2196f3 is 3.12:1. That is not theoretical: the booking dialog renders
 * `<Button color="error">Cancel booking</Button>`, whose label is drawn in
 * `error.main`, so a destructive action sat at 3.68:1. `status.*` replaces all
 * four at 5.9–7.1:1.
 *
 * Only `main` is given for those four so MUI derives `light`/`dark` at the
 * theme's tonalOffset and picks `contrastText` itself — Alert's standard
 * variant derives both its tint and its label from `light`, and hand-picked
 * pairs drift apart from `main` the moment anyone edits one of them.
 */
const adminTheme = createTheme({
  palette: {
    primary: {
      main: brand.brown,           // 10.72:1 on white
      light: ink.secondary,        // 6.54:1 — hover and de-emphasised states
      dark: brand.brownDark,       // 15.71:1 — active states
      contrastText: surface.card,  // 10.72:1 white on brown
    },
    secondary: {
      // Deep rust. Deliberately NOT brand.clay (#C25B3F): clay is 4.32:1 on
      // white and `secondary.main` lands on Button and Chip labels, so it has
      // to clear 4.5 as text. Clay stays a background colour only.
      main: '#7D341C',   // 8.82:1
      light: '#923D20',  // 7.22:1
      dark: '#692B17',   // 10.72:1
      contrastText: surface.card,
    },
    background: {
      default: surface.card,
      paper: surface.card,
    },
    text: {
      // was #2a1109 / #542312 — 17.76 and 12.91, both so dark that primary and
      // secondary were indistinguishable and the hierarchy read as flat.
      primary: ink.primary,     // 10.72:1
      secondary: ink.secondary, // 6.54:1
    },
    divider: line.default,
    success: { main: status.success }, // 6.27:1 (was #4caf50 — 2.78:1)
    warning: { main: status.warning }, // 5.92:1 (was #ff9800 — 2.16:1)
    error: { main: status.error },     // 7.07:1 (was #f44336 — 3.68:1)
    info: { main: status.info },       // 6.85:1 (was #2196f3 — 3.12:1)
  },

  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0,
    },
    // One weight/size ramp so headings step down visibly instead of all
    // arriving as "bold dark brown at some size".
    h1: { color: ink.primary, fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { color: ink.primary, fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { color: ink.primary, fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { color: ink.primary, fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { color: ink.primary, fontWeight: 700 },
    h6: { color: ink.primary, fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 700, letterSpacing: '0.01em' },
    body2: { lineHeight: 1.55 },
    caption: { lineHeight: 1.45 },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 16,
        },
        // Scoped to *Primary so an explicit `color="error"` or `color="success"`
        // button actually renders in its own colour. `color` defaults to
        // 'primary', so a plain <Button variant="contained"> is still brown.
        containedPrimary: {
          backgroundColor: brand.brown,
          color: surface.card,
          '&:hover': { backgroundColor: brand.brownDark },
          '&:active': { backgroundColor: darken(brand.brownDark, 0.25) },
        },
        outlinedPrimary: {
          // A button's outline *is* its boundary, so it has to clear the 3:1
          // WCAG 1.4.11 floor. line.strong is only 1.80:1 on white;
          // alpha(brown, 0.6) is 3.40:1 and still lighter than a solid rule.
          borderColor: alpha(brand.brown, 0.6),
          color: brand.brown,
          '&:hover': {
            borderColor: brand.brown,
            backgroundColor: surface.sunken,
          },
        },
        textPrimary: {
          color: brand.brown,
          '&:hover': { backgroundColor: surface.sunken },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            // was #e39a82 on hover — 2.16:1, *lighter* than the resting
            // outline, so hovering made the field harder to see, not easier.
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: line.strong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.brown,
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: brand.brown },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
        filled: {
          backgroundColor: brand.brown,
          color: surface.card,
          '&:hover': { backgroundColor: brand.brownDark },
        },
        outlined: {
          borderColor: alpha(brand.brown, 0.6), // 3.40:1 boundary
          color: brand.brown,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: surface.card,
          borderRadius: 12,
          border: `1px solid ${line.subtle}`,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${line.subtle}`, minHeight: 44 },
        // 5.62:1 against white, so the selected tab is obvious without relying
        // on weight alone. Mustard proper (#D8B510) is 1.99:1 and would be an
        // indicator most people cannot see.
        indicator: { backgroundColor: brand.mustardInk, height: 3, borderRadius: 3 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          color: ink.secondary,
          '&.Mui-selected': { color: brand.brown, fontWeight: 700 },
        },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: line.subtle } },
    },

    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 12 } },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700, color: ink.primary },
      },
    },

    MuiAlert: {
      styleOverrides: { root: { borderRadius: 8, alignItems: 'center' } },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: surface.darker,
          color: ink.onDark,      // 13.89:1 on the near-black brown
          fontSize: '0.75rem',
          lineHeight: 1.4,
          padding: '6px 10px',
        },
        arrow: { color: surface.darker },
      },
    },
  },

  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  spacing: 8,
  shape: { borderRadius: 8 },
});

export default adminTheme;
